import { authClient, clearAuthToken, restoreAuthToken } from "@/app/lib/auth-client";
import { openExternal } from "@/app/platform";
import { env } from "@/app/config/env";
import {
  API_ERRORS,
  toAuthReason,
  AUTH_REASON_FIELD,
  type ApiErrorCode,
  type ApiResult,
} from "@shared/contracts";
import { toSessionUser } from "./auth.converter";
import type { SessionUserModel } from "./auth.model";

/**
 * Everything the app can do with an identity, expressed as results rather than thrown
 * errors — a rejected promise in a click handler is an unhandled rejection two refactors
 * later.
 */
/**
 * THE AUTH LIBRARY THROWS WHEN THE NETWORK FAILS — it does not return its `{ data, error }`
 * shape for a transport failure, only for an answer the service actually gave.
 *
 * Nothing caught that, so a sign-in attempted offline (or blocked by CORS, or against a
 * service that is down) rejected with a bare `TypeError: Load failed` carrying no error
 * code — and a form that renders its message FROM the code therefore rendered nothing. The
 * user pressed "Sign in", nothing happened, and there was no way to tell why.
 *
 * Every call goes through here so that a thrown transport failure becomes the same
 * `NETWORK_UNREACHABLE` the rest of the app already knows how to word.
 */
async function attempt<T>(
  call: () => Promise<{
    data: T | null;
    error?: { message?: string; code?: string; status?: number } | null;
  }>,
): Promise<
  | { data: T | null; failed: false }
  | { failed: true; message?: string; code?: string; status?: number; transport: boolean }
> {
  try {
    const { data, error } = await call();
    if (error)
      return {
        failed: true,
        message: error.message,
        code: error.code,
        status: error.status,
        transport: false,
      };
    return { data, failed: false };
  } catch (error) {
    return { failed: true, message: error instanceof Error ? error.message : undefined, transport: true };
  }
}

/**
 * NOT EVERY REFUSAL FROM THE IDENTITY SERVICE IS A BAD PASSWORD.
 *
 * The library answers a throttled attempt, a crashed service and a genuinely wrong password
 * through the same channel, and only the HTTP status tells them apart. Ignoring it — as this
 * did — meant an overloaded server told the user to check their password, and a rate limit
 * told them the same. Both are dead ends: the password is fine and retyping it will not
 * help.
 *
 * The status is the ONLY reliable discriminator here, because the library's `code` is absent
 * for anything it did not itself decide.
 */
function refusalCode(status: number | undefined, refused: ApiErrorCode): ApiErrorCode {
  if (status === undefined) return refused;
  if (status === 429) return API_ERRORS.RATE_LIMITED;
  if (status === 403) return API_ERRORS.FORBIDDEN;
  if (status >= 500) return API_ERRORS.SERVER_ERROR;
  return refused;
}

/**
 * THE LIBRARY'S REFUSAL, TURNED INTO SOMETHING A FIELD CAN SAY.
 *
 * The auth library answers a refused sign-up with a specific reason — the password is too
 * short, that email is taken — and all of it was being flattened to one `VALIDATION_FAILED`,
 * which the form rendered as the app-wide "check the fields" sentence. So a user whose
 * password was one character short was told to check the fields, with nothing marking which
 * one or why.
 *
 * The reason travels in `fields`, keyed by the input it belongs beside, and the form words
 * it there. An unrecognised code is dropped rather than shown: the library's own prose is
 * English-only and occasionally leaks internals.
 */
function reasonFields(code: string | undefined) {
  const reason = toAuthReason(code);
  return reason ? { [AUTH_REASON_FIELD[reason]]: reason } : undefined;
}

export const authService = {
  async currentUser(): Promise<ApiResult<SessionUserModel | null>> {
    const token = await restoreAuthToken();
    const result = await attempt(() => authClient.getSession());
    if (result.failed) return { ok: false, error: API_ERRORS.NETWORK_UNREACHABLE };

    const user = (result.data as { user?: Parameters<typeof serialiseUser>[0] } | null)?.user;
    if (!user) return { ok: true, data: null };

    /**
     * A SESSION WE CANNOT USE IS NOT A SESSION.
     *
     * The service will answer this call from a COOKIE, while every other request in the app
     * carries a BEARER token — and the session check does not hand a bearer token back
     * (verified: the response carries no `set-auth-token`). So "signed in by cookie, no
     * token stored" is a state the app can genuinely reach, and it cannot heal itself: the
     * app believes it is signed in, every request 401s, the 401 handler asks the service,
     * the service says the session is fine, and the screen stays broken forever.
     *
     * It is not hypothetical on a phone. The Keychain item is deliberately
     * `ThisDeviceOnly`, so a phone restored from a backup comes back with the web view's
     * cookie and without the token.
     *
     * Treating it as signed out costs one sign-in and is always recoverable. The
     * alternative is an app that looks signed in and does nothing.
     */
    if (!token) {
      await authService.signOut();
      return { ok: true, data: null };
    }

    return { ok: true, data: toSessionUser(serialiseUser(user)) };
  },

  async signIn(email: string, password: string): Promise<ApiResult<SessionUserModel>> {
    const result = await attempt(() => authClient.signIn.email({ email, password }));
    if (result.failed) {
      // A refused password and an unreachable service are different problems with different
      // fixes, and the user is told which one they have.
      return {
        ok: false,
        error: result.transport
          ? API_ERRORS.NETWORK_UNREACHABLE
          : refusalCode(result.status, API_ERRORS.UNAUTHENTICATED),
        message: result.message,
        fields: reasonFields(result.code),
      };
    }
    const user = (result.data as { user?: Parameters<typeof serialiseUser>[0] } | null)?.user;
    if (!user) return { ok: false, error: API_ERRORS.UNAUTHENTICATED };
    return { ok: true, data: toSessionUser(serialiseUser(user)) };
  },

  async signUp(name: string, email: string, password: string): Promise<ApiResult<SessionUserModel>> {
    const result = await attempt(() => authClient.signUp.email({ name, email, password }));
    if (result.failed) {
      return {
        ok: false,
        error: result.transport
          ? API_ERRORS.NETWORK_UNREACHABLE
          : refusalCode(result.status, API_ERRORS.VALIDATION_FAILED),
        message: result.message,
        fields: reasonFields(result.code),
      };
    }
    const user = (result.data as { user?: Parameters<typeof serialiseUser>[0] } | null)?.user;
    if (!user) return { ok: false, error: API_ERRORS.VALIDATION_FAILED };
    return { ok: true, data: toSessionUser(serialiseUser(user)) };
  },

  async signOut(): Promise<void> {
    await authClient.signOut().catch(() => {});
    await clearAuthToken();
  },

  /**
   * SOCIAL SIGN-IN ON A PHONE IS A ROUND TRIP OUT OF THE APP AND BACK.
   *
   * The provider will not render inside our web view (Google refuses outright, and Apple's
   * sheet needs the system), so the flow is: ask the service for the provider URL, open the
   * system browser, let the service finish the exchange, and have it hand control back
   * through this app's own URL scheme carrying the session token. The link arrives at
   * src/app/lib/deep-links.ts, which stores the token and refreshes the session.
   *
   * ⚠️ Sign in with Apple is REQUIRED by App Store review as soon as any other social
   * provider is offered. Both are wired here; the service decides which are enabled.
   */
  async signInWithProvider(provider: "apple" | "google"): Promise<ApiResult<null>> {
    const callbackURL = `${env.apiUrl}/v1/auth/native-return?scheme=${encodeURIComponent(nativeScheme())}`;

    // Through `attempt()` like every other method here. It was the ONE call that was not,
    // so a social sign-in started offline rejected out of a method typed to return a
    // result — the unhandled rejection the ApiResult envelope exists to make impossible.
    const result = await attempt(() =>
      authClient.signIn.social({ provider, callbackURL, disableRedirect: true }),
    );
    if (result.failed)
      return {
        ok: false,
        error: result.transport ? API_ERRORS.NETWORK_UNREACHABLE : API_ERRORS.SERVER_ERROR,
      };

    const url = (result.data as { url?: string } | null)?.url;
    if (!url) return { ok: false, error: API_ERRORS.SERVER_ERROR };

    await openExternal(url);
    return { ok: true, data: null };
  },
};

/** The app's own URL scheme for this build — three identities, three schemes. */
export function nativeScheme(): string {
  const suffix = env.appEnv === "production" ? "" : `.${env.appEnv}`;
  return `${import.meta.env.VITE_APP_SCHEME ?? "app"}${suffix}://auth`;
}

/** The auth library returns dates as Date or string depending on transport. Normalise. */
function serialiseUser(user: {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified: boolean;
  createdAt: Date | string;
}) {
  return {
    id: user.id,
    email: user.email,
    name: user.name ?? null,
    imageUrl: user.image ?? null,
    emailVerified: user.emailVerified,
    createdAt:
      typeof user.createdAt === "string" ? user.createdAt : user.createdAt.toISOString(),
  };
}
